
import { useCallback, useEffect, useState } from 'react';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';
import { useAdelantoCart } from '@/contexts/AdelantoCartContext';

export interface MelBalance {
  liquidMel: number;
  outstanding: number;
  available: number;
}

const formatCLP = (n: number) => n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

export default function MelBalanceWidget() {
  const [balance, setBalance] = useState<MelBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const { registerOnSolicitudConfirmada } = useAdelantoCart();

  const fetchBalance = useCallback(async () => {
    try {
      const url = getEndpointUrl('mel', 'balance');
      const data = await httpClient.get<MelBalance>(url, { shouldCache: false });
      setBalance(data);
    } catch {
      setBalance(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Refrescar saldo cuando se confirma un adelanto desde el CartWidget — sin
  // esto el widget queda mostrando la línea pre-adelanto hasta que el comercio
  // navega a otra ruta y vuelve.
  useEffect(() => {
    return registerOnSolicitudConfirmada(() => {
      fetchBalance();
    });
  }, [registerOnSolicitudConfirmada, fetchBalance]);

  if (loading) {
    return (
      <div className="rounded-xl border bg-card p-6 shadow-sm animate-pulse">
        <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded mb-4" />
        <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
      </div>
    );
  }

  if (!balance) {
    return null;
  }

  const { liquidMel, outstanding, available } = balance;
  const pct = liquidMel > 0 ? Math.min(100, Math.max(0, (outstanding / liquidMel) * 100)) : 0;

  return (
    <div className="rounded-xl border bg-card p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground">Tu línea de adelantos</h3>
          <p className="mt-1 text-2xl font-semibold text-foreground">{formatCLP(available)}</p>
          <p className="mt-1 text-xs text-muted-foreground">disponible</p>
        </div>
        <span className="material-symbols-outlined rounded-lg bg-flux-primary/10 p-2 text-flux-primary">account_balance_wallet</span>
      </div>

      <div className="mt-5 space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Línea total</span>
          <span className="font-medium text-foreground">{formatCLP(liquidMel)}</span>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Usada (adelantos vigentes)</span>
          <span className="font-medium text-foreground">{formatCLP(outstanding)}</span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-flux-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}
